import { Fragment, useContext } from 'react';
import type { FC, ReactNode } from 'react';
import classnames from 'classnames';
import { LocaldataContext } from 'react-localdata';
import { CSSTransition } from 'react-transition-group';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react';

import UserContext from 'app/common/UserContext';

import style from './style.scss';

interface Props {
  displayed: boolean;
  children: ReactNode;
  className?: string;
  onClickOutside?: () => void;
  initialFocus?: number;
  overlayClassName?: string;
  withPortal?: boolean;
}

const animationClassNames = {
  appear: style.enter,
  appearActive: style.enterActive,
  enter: style.enter,
  enterActive: style.enterActive,
  exit: style.exit,
  exitActive: style.exitActive
};

const Modal: FC<Props> = ({
  displayed,
  children,
  onClickOutside,
  className,
  overlayClassName,
  withPortal = true,
  initialFocus = -1 // default from floating-ui doc
}) => {
  const localDataContext = useContext(LocaldataContext);
  const userContextData = useContext(UserContext);

  const { floating, context } = useFloating({
    open: displayed,
    onOpenChange: onClickOutside
  });

  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    escapeKey: true
  });
  const role = useRole(context, { role: 'dialog' });

  const { getFloatingProps } = useInteractions([dismiss, role]);

  const Wrapper = withPortal ? FloatingPortal : Fragment;

  return (
    <UserContext.Provider value={userContextData}>
      <LocaldataContext.Provider value={localDataContext}>
        <CSSTransition
          timeout={300}
          in={displayed}
          classNames={animationClassNames}
          mountOnEnter
          unmountOnExit
          appear
        >
          <Wrapper>
            <FloatingFocusManager
              context={context}
              modal
              guards={false}
              initialFocus={initialFocus}
            >
              <FloatingOverlay className={classnames(style.overlay, overlayClassName)} lockScroll>
                <div
                  ref={floating}
                  className={classnames(style.container, className)}
                  onClick={e => e.stopPropagation()}
                  {...getFloatingProps()}
                >
                  {children}
                </div>
              </FloatingOverlay>
            </FloatingFocusManager>
          </Wrapper>
        </CSSTransition>
      </LocaldataContext.Provider>
    </UserContext.Provider>
  );
};

Modal.displayName = 'Modal';

export default Modal;
